import { Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import type { ComponentPropsWithRef, ElementType } from 'react';
import { FormattedMessage } from 'react-intl';
import { IconNames } from 'sprite/types';

import LinkUnstyled from 'components/@common/LinkUnstyled';
import Icon from 'components/@icons/Icon';

type Props<T extends ElementType> = {
  component?: T;
} & ComponentPropsWithRef<T> & {
    iconName: IconNames;
    intlId: string;
    onClick?: () => void;
    sx?: SxProps<Theme>;
  };

const SettingsLink = <T extends ElementType = typeof LinkUnstyled | 'div'>({
  component,
  iconName,
  intlId,
  onClick,
  sx,
  ...rest
}: Props<T>) => {
  const theme = useTheme();

  return (
    <Box
      component={component}
      sx={[
        {
          textAlign: 'start',
          gap: 1,
          py: 1,
          alignItems: 'center',
          display: 'flex',
          textDecoration: 'none',
          '&& svg, && p': {
            transition: theme.transitions.create(['color']),
          },
          '@media (hover: none)': {
            '&:active': {
              color: theme.vars.palette.grey[500],
              '&& svg, && p': {
                color: theme.vars.palette.grey[500],
              },
            },
          },
          '@media (hover: hover)': {
            '&:hover': {
              color: theme.vars.palette.grey[500],
              '&& svg, && p': {
                color: theme.vars.palette.grey[500],
              },
            },
            '&:active': {
              color: theme.vars.palette.grey[600],
              '&& svg, && p': {
                color: theme.vars.palette.grey[600],
              },
            },
          },
          '&:hover, &:active': {
            backgroundColor: 'unset',
            textDecoration: 'none',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={onClick}
      {...rest}
    >
      <Icon name={iconName} fontSize={20} color="text.secondary" />
      <Typography
        variant="small"
        sx={[
          {
            flexGrow: 1,
            color: 'text.secondary',
          },
          (theme) => ({
            position: 'relative',
            top: theme.direction === 'ltr' ? 2 : 0,
          }),
        ]}
      >
        <FormattedMessage id={intlId} />
      </Typography>
      <Icon
        rotateInArabic
        name="ChevronRight"
        fontSize={20}
        color="text.secondary"
      />
    </Box>
  );
};

export default SettingsLink;
